import moment from 'moment';

export const isObject = obj => obj != null && typeof obj == 'object';

export const isString = v => typeof v == 'string';

export const isFunction = v => typeof v == 'function';

export const noop = () => {};
/**
 * @template T
 * @param {T} obj
 * @returns {T}
 */
export const deepCopy = obj => {
  if (!isObject(obj)) return obj;
  const clone = Array.isArray(obj) ? [] : {};
  for (const key in obj) {
    const v = obj[key];
    clone[key] = typeof v == 'object' && v != null ? deepCopy(v) : v;
  }
  return clone;
};

/**
 * Object equals
 * @param {*} target1
 * @param {*} target2
 * @returns {Boolean}
 */
export const equals = (target1, target2) => {
  if (isObject(target1) && isObject(target2)) {
    const keys = Object.keys(target1);
    return !keys.length
      ? Object.keys(target2).length == 0 &&
          JSON.stringify(target1) == JSON.stringify(target2)
      : keys.every(key =>
          isObject(target1[key])
            ? equals(target1[key], target2[key])
            : target1[key] === target2[key],
        ) && keys.length == Object.keys(target2).length;
  } else return target1 === target2;
};
/**
 *
 * @param {Objcet} obj
 * @param {string} key
 * @returns
 */
export const findByHasKey = (obj, key) => {
  if (!isObject(obj) || !key) return undefined;
  const hasKey = obj.hasOwnProperty(key);
  return hasKey ? obj : Object.values(obj).find(v => findByHasKey(v, key));
};

/**
 * 중복제거
 * @template T
 * @param {T []} arr
 * @returns {T []}
 */
export const deduplicate = arr => {
  if (!Array.isArray(arr)) return arr;
  try {
    return arr.reduce((a, c) => {
      !a.some(v => equals(v, c)) && a.push(c);
      return a;
    }, []);
  } catch (error) {
    console.log(error);
    return arr;
  }
};

/**
 * @template T
 * @param {T} target
 * @param {*} source
 * @param {boolean} isArrayMerge
 * @returns {T}
 */
export const merge = (target, source, isArrayMerge = false) => {
  if (!isObject(target) || !isObject(source)) {
    return target;
  }
  Object.keys(source).forEach(key => {
    const tv = target[key];
    const sv = source[key];
    if (Array.isArray(tv) && Array.isArray(sv)) {
      target[key] = isArrayMerge ? deduplicate([...tv, ...sv]) : sv;
    } else if (isObject(tv) && isObject(sv)) {
      target[key] = merge(Object.assign({}, tv), sv);
    } else {
      target[key] = sv;
    }
  });
  return target;
};
/**
 * 테스트용 sleep()
 * @param {number} delay
 * @returns
 */
export const wait = (delay = 3000) =>
  new Promise(timeout => setTimeout(timeout, delay));

export const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random16 = (Math.random() * 16) | 0;
    const value = char == 'x' ? random16 : (random16 & 0x3) | 0x8;
    return value.toString(16);
  });

export const addTaskQueue = (fn = noop, delay = 0) =>
  new Promise((timeout, reject) => {
    setTimeout(async () => {
      try {
        if (fn instanceof Promise || typeof fn !== 'function') {
          return timeout(await fn);
        }
        timeout(await fn());
      } catch (error) {
        reject(error);
      }
    }, delay);
  });

export const createDebounce = () => {
  let KEY;
  return (callback, delay = 200) => {
    clearTimeout(KEY);
    KEY = setTimeout(callback, delay);
  };
};

export const createThrottle = (delay = 200) => {
  let flag = true;

  return callback => {
    if (flag) {
      flag = false;
      callback();
      setTimeout(() => {
        flag = true;
      }, delay);
    }
  };
};

export const debounce = (() => {
  const cache = {};
  return (callback, key, delay = 200) => {
    clearTimeout(cache[key]);
    cache[key] = setTimeout(callback, delay);
  };
})();

export const groupBy = (obj, key, callback) => {
  if (!isObject(obj) || !key) return obj;
  const group = Object.keys(obj).reduce((a, c) => {
    const item = obj[c];
    if (a[item[key]]) a[item[key]].push(item);
    else a[item[key]] = [item];
    return a;
  }, {});
  return !callback
    ? group
    : Object.entries(group).reduce(
        (a, [key, value], i, origin) =>
          Object.assign(a, { [key]: callback(value, key, i, origin) }),
        {},
      );
};

export const kb = 1024;
export const mb = kb * kb;
// const gb = mb * kb; 이거까진 사용 하지 않을거같음
/**
 *
 * @param {number} byteSize byte 단위
 * @returns
 */
export const formatFileSize = byteSize => {
  if (isNaN(byteSize)) return byteSize;
  const size = Number(byteSize);
  const fomatter = size / mb >= 1 ? mb : kb;

  return `${(size / fomatter).toFixed(2)} ${fomatter == mb ? 'MB' : 'KB'}`;
};

export const random = max => (Math.random() * max) | 0;

export const formatDate = (date, format = 'YYYY-MM', defaultValue) => {
  try {
    return moment(date).format(format);
  } catch (error) {
    console.warn(error);
    return defaultValue ?? date;
  }
};
export const copy = text => navigator.clipboard.writeText(text);

export const removeTag = (text = '') =>
  text
    .replace(/<p>|<\/p>/gi, match => (match === '</p>' ? '\n' : ''))
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/&nbsp;/g, '');

export const addComma = num =>
  num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const sum = (...num) => num.flat().reduce((a, b) => a + b, 0);

export const avg = (...num) => {
  const flat = num.flat();
  return flat.length ? sum(...flat) / flat.length : 0;
};

/**
 * @template T
 * @param {Array<T>} list
 * @returns {()=>T} getNext
 */
export const infinityArray = list => {
  const clone = deepCopy(list);
  let cursor = 0;
  return () => clone[cursor++ % clone.length];
};

/**
 * @template T
 * @param {Array<T>} list
 * @returns {Array<T>}
 */
export const shuffleArray = list => {
  const clone = deepCopy(list);
  for (let i = clone.length - 1; i > 0; i--) {
    const target = Math.floor(Math.random() * (i + 1));

    [clone[i], clone[target]] = [clone[target], clone[i]];
  }
  return clone;
};
import mime from 'mime';

export const getFileType = contentType => {
  switch (contentType) {
    case 'application/haansoftpdf':
    case 'application/pdf':
    case 'application/vnd.epapyrus.pdf':
    case 'application/vnd.epapyrus.plugin.pdf':
    case 'application/x-pdf':
    case 'pdf':
      return 'pdf';
    case 'image/gif':
    case 'image/jpeg':
    case 'image/jpg':
    case 'image/png':
      return 'imageFile';
    case 'application/haansoftxls':
    case 'application/haansoftxlsx':
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'excel';
    default:
      return mime.getExtension(contentType);
  }
};

export const getFileIcon = fileType => {
  switch (fileType) {
    case 'pdf':
      return {
        icon: 'mdi-file-pdf-box',
        color: '#F4511E',
      };
    case 'powerpoint':
      return {
        icon: 'mdi-microsoft-powerpoint',
        color: '#F4511E',
      };
    case 'imageFile':
      return {
        icon: 'mdi-file-image-outline',
        color: '#9FA8DA',
      };
    case 'excel':
      return {
        icon: 'mdi-microsoft-excel',
        color: '#43A047',
      };
    case 'word':
      return {
        icon: 'mdi-microsoft-word',
        color: '#1976D2',
      };

    default:
      return undefined;
  }
};
